import { Avatar, CardContent, Grid, Pagination, Typography } from "@mui/material";
import { AppGlobalClassKeys } from "app/appGlobalStyles";
import { AxiosResponse } from "axios";
import { MprcButton, MprcCard, MprcCardActions, MprcContainer, MprcRequestErrorDialog } from "components";
import { MprcListItemTitle } from "components/MprcListItemTitle";
import { MprcFilterCount } from "components/filter";
import { IconButtonArrowRight } from "components/iconButtons/IconButtonArrowRight";
import { IconAnglesRight } from "components/icons";
import { StringImportedItemModel, UserInfoDto, UserInfoDtoPaginatedList, UserStatus } from "generated/CoreApi_v1";
import { useModalUserProfile } from "providers/UserProfileProvider";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { BatchEditImportUserDialog } from "views/foundation/user/components/BatchEdit/BatchEditImportUserDialog";
import { BatchEditSearchUsersFilter } from "views/foundation/user/components/BatchEdit/BatchEditSearchUsersFilter";
import { IAdvFilterCommand, IAdvFilterParams } from "views/general/AdvancedFilter";

////////////////////////////////////////////////////////////////////////////

type MenuImportOptions = "importFile" | "importList" | "clear";

export interface IBatchOperationFilter {
    pageNumber: number;
    filter: IAdvFilterParams<any>;
}

interface IProps {
    viewData?: UserInfoDtoPaginatedList;
    viewFilter: IBatchOperationFilter;
    filterCommands: IAdvFilterCommand[];
    additionalFilterButtons?: React.ReactNode[];
    importedFile: boolean;
    onChangeFilter: (filter: Partial<IBatchOperationFilter>) => void;
    onResetFilter: () => void;
    onAddUser: (user: UserInfoDto) => void;
    onAddAllUsers: () => void;
    onClearImportedUsers: () => void;
    onImportFile: (fileId: string) => Promise<AxiosResponse<StringImportedItemModel[]>>;
    onImportList: (users: string[]) => Promise<AxiosResponse<StringImportedItemModel[]>>;
    onCompleteImport: (success: boolean) => void;
}

export const BatchEditSearchUsers: React.FC<IProps> = (props) => {

    const { viewData, viewFilter, filterCommands, additionalFilterButtons,
        importedFile,
        onCompleteImport,
        onChangeFilter,
        onResetFilter,
        onAddUser,
        onAddAllUsers,
        onClearImportedUsers,
        onImportFile,
        onImportList
    } = props;

    const { t } = useTranslation("core");
    const { showUser } = useModalUserProfile();

    const [viewError, setViewError] = useState<any>();
    const [dialog, setDialog] = useState<any>();

    var advFilter = viewFilter.filter.advFilters;
    const filtered = advFilter.length > 0;

    ///
    const handleImportMenuOption = (option: MenuImportOptions) => {
        switch (option) {
            case "importFile":
            case "importList":
                setDialog(
                    <BatchEditImportUserDialog
                        importType={option == "importFile" ? "file" : "list"}
                        onClose={(success) => {
                            onCompleteImport(success);
                            setDialog(null);
                        }}
                        onImportFile={onImportFile}
                        onImportList={onImportList}
                    />);
                break;
            case "clear":
                onClearImportedUsers();
                break;
        }
    };

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    return (
        <>
            <MprcRequestErrorDialog error={viewError} onClose={() => setViewError(null)} />

            {dialog}

            <MprcContainer>
                <MprcCard>
                    <BatchEditSearchUsersFilter
                        filterOptions={viewFilter.filter}
                        additionalFilterButtons={additionalFilterButtons}
                        onFilterChange={(filter) => onChangeFilter({ filter: filter })}
                        onImportedFile={handleImportMenuOption}
                        filterCommands={filterCommands}
                        importedFile={importedFile}
                    />
                    <CardContent>
                        {viewData &&
                            <MprcContainer>
                                <Grid container>
                                    <Grid size="grow">
                                        <MprcFilterCount
                                            loading={false}
                                            isEmpty={viewData.totalRows == 0}
                                            countText={t("User.SearchUsers.RecordsFoundText", { count: viewData.totalRows })}
                                            filter={{
                                                active: filtered,
                                                text: t("shared:Filter.FilteredRecords", { count: viewData.totalRows }),
                                                onResetFilter: onResetFilter
                                            }}
                                        />
                                    </Grid>
                                    {
                                        viewData.totalRows > 0 &&
                                        <Grid>
                                            <MprcButton
                                                text={t("User.SearchUsers.AddAllButtonText")}
                                                title={t("User.SearchUsers.AddAllButtonTitle")}
                                                color="highlight"
                                                onClick={onAddAllUsers}
                                                endIcon={<IconAnglesRight />}
                                            />
                                        </Grid>
                                    }
                                </Grid>
                            </MprcContainer>
                        }
                        {
                            viewData && viewData.totalRows > 0 && (
                                <>
                                    <Grid container justifyContent="center" alignItems="center" spacing={2}>
                                        {viewData.items.map(item => {
                                            return (
                                                <Grid key={item.userId} size={{ xs: 12 }}>
                                                    <Grid container justifyContent="center" alignItems="center" spacing={2} wrap="nowrap">
                                                        <Grid alignSelf="flex-start">
                                                            <Avatar
                                                                className={AppGlobalClassKeys.clickable}
                                                                sx={{ width: 48, height: 48 }}
                                                                src={item.thumbnailUrl == null ? undefined : item.thumbnailUrl}
                                                                onClick={() => showUser(item.userId)}
                                                            />
                                                        </Grid>
                                                        <Grid size="grow">
                                                            <Grid container direction="column" wrap="nowrap">
                                                                <Grid>
                                                                    <MprcListItemTitle onClick={() => onAddUser(item)}>
                                                                        {item.name}
                                                                    </MprcListItemTitle>
                                                                </Grid>
                                                                <Grid>
                                                                    {
                                                                        item.status == UserStatus.ActiveWithLoginBlocked &&
                                                                        <Typography variant="bodyNotice">
                                                                            {t("User.SearchUsers.UserBlockedText")}
                                                                        </Typography>
                                                                    }
                                                                </Grid>
                                                            </Grid>
                                                        </Grid>
                                                        <Grid>
                                                            <IconButtonArrowRight
                                                                color="highlight"
                                                                size="medium"
                                                                onClick={() => onAddUser(item)}
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                            );
                                        })}
                                    </Grid>
                                    <MprcCardActions>
                                        {viewData && viewData.totalPages > 1 && (
                                            <Pagination
                                                shape="rounded"
                                                color="primary"
                                                page={viewData.pageNumber}
                                                count={viewData.totalPages}
                                                onChange={(e, pageNumber) => {
                                                    if (pageNumber != viewFilter.pageNumber) {
                                                        onChangeFilter({ pageNumber });
                                                    }
                                                }}
                                            />
                                        )}
                                    </MprcCardActions>
                                </>
                            )}
                    </CardContent>
                </MprcCard>
            </MprcContainer>
        </>
    );
};
